import { ref, computed, readonly, useContext } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type { UseShippingCostErrors, UseShippingCostInterface } from './useShippingCost';
import { useApi } from '~/composables';
import type { getProductShippingCostQuery, ProductShippingCostData } from './types/getProductShippingCostData';
import getProductShippingCostData from './query/getProductShippingCostData.gql';

import { useShippindCostStore } from './store/shippingCost';

/**
 * Allows loading and changing the currency.
 *
 * See the {@link UseShippingCostInterface} for a list of methods and values available in this composable.
 */
export function useShippingCost(): UseShippingCostInterface {
  const { app } = useContext();
  const { query } = useApi();
  const loading = ref(false);

  const shippingCostStore = useShippindCostStore();
  const shippingCost = computed(() => shippingCostStore.shippingCost);

  const error = ref<UseShippingCostErrors>({
    load: null,
  });

  const load = async () => {
    error.value.load = null;
    loading.value = true;

    Logger.debug('useShippingCost/load');

    try {
      let dummySku = 'test';
      const { data, errors } = await query<getProductShippingCostQuery>(getProductShippingCostData, { sku: dummySku });

      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(',');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }

      let results = data?.shippingCost || null;

      shippingCostStore.$patch((state) => {
        state.shippingCost = results;
      });
    } catch (err) {
      Logger.debug('[ERROR] useShippingCost/load', err);
      error.value.load = err;
    } finally {
      loading.value = false;
    }
  };

  return {
    load,
    shippingCost: readonly(shippingCost),
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useShippingCost';

export default useShippingCost;
